import PropTypes from "prop-types";
import CustomMoreOptions from "@/components/specific/Custom_More_Options";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import Delete from "@/components/common/alerts/Delete";
import { Dropdown } from "@/config/antd";
import { useDispatch } from "react-redux";
import { useLoader } from "@/hooks/LoaderProvider";
import { deleteTownHall } from "@/store/redux/slices/townhall/townhall_slice";

const TownHallCard = ({
  _id,
  title,
  date,
  status,
  onDelete,
  onModify,
  onCardChange,
}) => {
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();

  const handleMenuClick = (menuItem, e) => {
    e?.domEvent?.stopPropagation();
    if (menuItem.key === "delete") {
      setDeleteModalVisible(true);
    } else if (menuItem.key === "modify") {
      onModify(_id);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleteModalVisible(false);
      showLoader();

      // Dispatch the delete action
      await dispatch(deleteTownHall(_id)).unwrap();
      hideLoader();

      // Call the parent onDelete callback with both id and title
      if (onDelete) {
        onDelete(_id, title);
      }

      // Let the parent component handle any refresh needed
      if (onCardChange) {
        onCardChange();
      }
    } catch (error) {
      console.error("Failed to delete town hall:", error);
      hideLoader();
    }
  };

  const handleCardClick = (e) => {
    // Prevent navigation if clicking on more options
    if (e.target.closest(".more-options")) return;
    navigate(`/dashboard/townhall/${_id}`);
  };

  return (
    <>
      <div
        className="rounded-lg p-4 shadow-sm bg-[var(--header-bg)]"
        onClick={handleCardClick}
        data-testid="townhall-card">
        {/* Header with title, status and more options */}
        <div className="flex justify-between gap-3">
          {/* Title and Date stacked vertically */}
          <div className="flex flex-col items-start">
            <h2 className="text-bold mb-2 truncate w-[200px]">{title}</h2>
            <h3 className="text-small text-lighterGrey">{date}</h3>
          </div>

          {/* Update status to show in red if expired */}
          <div className="flex items-center gap-1">
            <div
              className={`w-2 h-2 rounded-full ${
                status === "Expired" ? "bg-danger" : "bg-primary"
              }`}
            />
            <span
              className={`text-small ${
                status === "Expired" ? "text-danger" : "text-primary"
              } font-bold`}>
              {status}
            </span>
          </div>

          <Dropdown
            menu={{
              items: [
                {
                  key: "modify",
                  label: "Modify",
                  onClick: (e) => handleMenuClick({ key: "modify" }, e),
                },
                {
                  key: "delete",
                  label: "Delete",
                  onClick: (e) => handleMenuClick({ key: "delete" }, e),
                },
              ],
            }}
            trigger={["click"]}
            onClick={(e) => e.stopPropagation()}>
            <div className="more-options">
              <CustomMoreOptions variant="filled" orientation="vertical" />
            </div>
          </Dropdown>
        </div>
      </div>

      <Delete
        isVisible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onProceed={handleDeleteConfirm}
        header="Delete Town Hall?"
        paragraph={`Are you sure you want to delete "${title}"?`}
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </>
  );
};

TownHallCard.propTypes = {
  title: PropTypes.string,
  date: PropTypes.string,
  status: PropTypes.string,
  _id: PropTypes.string,
  onDelete: PropTypes.func,
  onModify: PropTypes.func,
  onCardChange: PropTypes.func,
};

export default TownHallCard;
